import React from 'react';

/**
 * Can put an element in full screen mode, or the whole document if no element is attached to ref.
 */
const useFullscreen = () => {
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [element, setElement] = React.useState<HTMLElement | null | undefined>(
    document.documentElement,
  );

  /**
   * A ref setter for the element to be put on fullscreen.
   */
  const ref = React.useCallback((node: HTMLElement | null) => {
    setElement(node);
  }, []);

  /**
   * Listen to fullscreenchange on the element.
   */
  React.useEffect(() => {
    const handler = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    element?.addEventListener('fullscreenchange', handler);

    return () => element?.removeEventListener('fullscreenchange', handler);
  }, [element]);

  const toggleFullscreen = React.useCallback(() => {
    if (!document.fullscreenElement) {
      element?.requestFullscreen();
    } else {
      document?.exitFullscreen();
    }
  }, [element]);

  return React.useMemo(
    () => ({ isFullscreen, toggleFullscreen, ref }),
    [ref, isFullscreen, toggleFullscreen],
  );
};

export default useFullscreen;
